import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { htmlSafe, type TrustedHTML } from "@ember/template";

import { balanceRows } from "#src/index.ts";

// balanceRows takes no image data — it justifies whatever direct children it is
// given based on their laid-out size. So the items here are plain content cards,
// not images. The aspect ratios come from `bento__cell--<shape>` classes rather
// than inline styles, so a container query can reshape the extreme ones as the
// container narrows (see demo-app/styles.css). Only the per-tile background
// stays inline, since it's arbitrary data.
interface Tile {
  label: string;
  value: string;
  shape: string;
  background: string;
}

const TILES: Tile[] = [
  {
    label: "Revenue",
    value: "$48.2k",
    shape: "wide",
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
  },
  {
    label: "Active users",
    value: "12,904",
    shape: "square",
    background: "linear-gradient(135deg, #0ea5e9, #22d3ee)",
  },
  {
    label: "Conversion",
    value: "3.8%",
    shape: "banner",
    background: "linear-gradient(135deg, #10b981, #34d399)",
  },
  {
    label: "Sessions",
    value: "88k",
    shape: "portrait",
    background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
  },
  {
    label: "Errors",
    value: "0.02%",
    shape: "square",
    background: "linear-gradient(135deg, #ef4444, #f87171)",
  },
  {
    label: "Latency",
    value: "142ms",
    shape: "landscape",
    background: "linear-gradient(135deg, #ec4899, #f472b6)",
  },
  {
    label: "Uptime",
    value: "99.98%",
    shape: "standard",
    background: "linear-gradient(135deg, #14b8a6, #2dd4bf)",
  },
  {
    label: "Storage",
    value: "1.4 TB",
    shape: "banner",
    background: "linear-gradient(135deg, #a855f7, #c084fc)",
  },
];

function cellBackground(tile: Tile): TrustedHTML {
  return htmlSafe(`background: ${tile.background};`);
}

// The cells, shared by both branches below so the only difference between the
// raw-CSS and balanced views is the balanceRows modifier.
const Cells = <template>
  {{#each TILES key="label" as |tile|}}
    <div class="bento__cell bento__cell--{{tile.shape}}" style={{cellBackground tile}}>
      <div class="bento__meta">
        <span class="bento__value">{{tile.value}}</span>
        <span class="bento__label">{{tile.label}}</span>
      </div>
    </div>
  {{/each}}
</template>;

export default class Bento extends Component {
  // On by default so the grid looks right; toggle it off to see the raw CSS
  // (fixed-height cards at their natural widths, with ragged rows) that the
  // modifier then justifies.
  @tracked balanced = true;
  // A local width control, so you can watch the cards re-pack without resizing
  // the window (which would reflow the whole page).
  @tracked width = 100;

  get stageStyle(): TrustedHTML {
    return htmlSafe(`width: ${this.width}%;`);
  }

  toggleBalanced = (event: Event): void => {
    this.balanced = (event.target as HTMLInputElement).checked;
  };

  setWidth = (event: Event): void => {
    this.width = Number((event.target as HTMLInputElement).value);
  };

  <template>
    <form class="demo-form">
      <label class="control">
        <input type="checkbox" checked={{this.balanced}} {{on "change" this.toggleBalanced}} />
        Balance rows
      </label>
      <label class="control">
        <span class="control-label">Width</span>
        <input type="range" min="30" max="100" value={{this.width}} {{on "input" this.setWidth}} />
        <span class="control-value">{{this.width}}%</span>
      </label>
    </form>

    <div style={{this.stageStyle}}>
      {{#if this.balanced}}
        <div
          class="bento"
          {{balanceRows selector=".bento__cell" aggressive=true preserve="aspect-ratio"}}
        >
          <Cells />
        </div>
      {{else}}
        <div class="bento">
          <Cells />
        </div>
      {{/if}}
    </div>
  </template>
}
